// Card metadata hydration for derived lists (tradeables, friend tradeables)
// whose rows only carry cardId/variant. Groups card ids by set and batch-
// fetches whole sets through the server-cached /api/cards/:setId endpoint.
// Direct sibling of the price cache in portfolioValue.js.

import PokemonTCGService from '../services/PokemonTCGService';

// cardId -> { name, image, imageLarge, setId, setName, number, rarity }
const cardCache = new Map();
const fetchedSetIds = new Set();
const inFlightFetches = new Map(); // setId -> Promise

// Card ids are `{setId}-{number}` and setIds can contain hyphens — always
// split on the LAST hyphen.
export const setIdFromCardId = (cardId) => cardId.slice(0, cardId.lastIndexOf('-'));

const CONCURRENCY = 3;

async function fetchSetIntoCache(setId) {
  if (fetchedSetIds.has(setId)) return;
  if (inFlightFetches.has(setId)) return inFlightFetches.get(setId);

  const promise = (async () => {
    try {
      const data = await PokemonTCGService.fetchSetCards(setId);
      for (const card of data.data || []) {
        cardCache.set(card.id, {
          name: card.name,
          image: card.images?.small || '',
          imageLarge: card.images?.large || '',
          setId: card.set?.id || setId,
          setName: card.set?.name || '',
          number: String(card.number ?? ''),
          rarity: card.rarity || null
        });
      }
      fetchedSetIds.add(setId);
    } catch (err) {
      // A failed set just stays unhydrated — callers render the cardId
      console.error(`cardIndex: failed to fetch set ${setId}:`, err);
    } finally {
      inFlightFetches.delete(setId);
    }
  })();

  inFlightFetches.set(setId, promise);
  return promise;
}

// Ensure metadata for all the given card ids is cached (batched per set,
// limited concurrency). Resolves when every needed set has been attempted.
export async function ensureCardsLoaded(cardIds) {
  const setIds = [...new Set(cardIds.map(setIdFromCardId))].filter(
    (id) => id && !fetchedSetIds.has(id)
  );

  for (let i = 0; i < setIds.length; i += CONCURRENCY) {
    await Promise.all(setIds.slice(i, i + CONCURRENCY).map(fetchSetIntoCache));
  }
}

export function getCardInfo(cardId) {
  return cardCache.get(cardId) || null;
}
